import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Button, Checkbox, Popover } from 'antd';
import { type FC, useState } from 'react';
import { useStore } from 'src/store/reducers';
import type { TrackerColumnKeys } from 'src/store/trackers/trackers_reducers';
import { useIsMobile } from '../../lib/customHooks';
import { IconTableList } from '../Icon';

interface Props {
  columns: Array<JSX.Element>;
  columnLabels: Array<{
    label: any;
    key: any;
  }>;
  checkedVisibleColumns: Set<TrackerColumnKeys>;
  setCheckedVisibleColumns: (value: Set<TrackerColumnKeys>) => void;
  setVisibleColumns: (value: Array<JSX.Element>) => void;
  dragElementType: string;
}

export const TrackerCollumnsVisibilityControlButton: FC<Props> = ({
  columns,
  columnLabels,
  checkedVisibleColumns,
  setCheckedVisibleColumns,
  setVisibleColumns,
  dragElementType,
}) => {
  const { changeSelectedCols } = useStore((s) => s.trackers.actions);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const isMobile = useIsMobile();

  return (
    <Popover
      trigger="click"
      overlayStyle={{ zIndex: 9999 }}
      open={popoverVisible}
      onOpenChange={(visible) => setPopoverVisible(visible)}
      content={
        <Checklist>
          {columnLabels.map((column) => (
            <ColumnSelector key={column.key}>
              <Checkbox
                checked={checkedVisibleColumns.has(column.key)}
                onChange={(e) => {
                  const newVisibleColumns = new Set(checkedVisibleColumns);
                  if (e.target.checked) {
                    newVisibleColumns.add(column.key);
                  } else {
                    newVisibleColumns.delete(column.key);
                  }
                  setCheckedVisibleColumns(newVisibleColumns);
                  setVisibleColumns(
                    columns.filter((col) => newVisibleColumns.has(col.props.dataKey)),
                  );
                  changeSelectedCols(newVisibleColumns);
                }}
              />
              {column.label}
            </ColumnSelector>
          ))}
        </Checklist>
      }
    >
      <ButtonFloat
        dragElementType={dragElementType}
        isMobile={isMobile}
        icon={<StyledIconTableList popoverVisible={popoverVisible} />}
      />
    </Popover>
  );
};

const ButtonFloat = styled(Button)<{ dragElementType: string; isMobile: boolean }>`
  top: ${({ dragElementType, isMobile }) =>
    dragElementType === 'total-trackers' ? (isMobile ? '42px' : '36px') : '1px'};

  height: 27px;
  width: 25px;
  right: 0px;
  position: absolute;

  &.ant-btn-variant-outlined,
  &.ant-btn-variant-dashed {
    border: none;
    background: rgb(244 244 244);
  }

  &.ant-btn.ant-btn-icon-only{
    width: 20px;
  }

  :hover {
    background: rgb(244 244 244) !important;
    border: none;
  }
`;

const Checklist = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColumnSelector = styled.div`
  display: flex;
  gap: 10px;
`;

const StyledIconTableList = styled(IconTableList)<{ popoverVisible: boolean }>`
  color: ${({ popoverVisible }) => (popoverVisible ? RacemapColors.PaleBlue : RacemapColors.Gray)};
  :hover {
    color: ${RacemapColors.PaleBlue};
  }
`;
